import PublicAdminPanelInstance from "../api/PublicAdminPanelInstance";
import { API_ROUTES } from "../api/ApiRoutes";
import { AFFILIATE_PORTAL_SIGN_IN_ROUTE } from "../routing/affiliatePortalRoutes";
import { REGULATOR_PORTAL_SIGN_IN_ROUTE } from "../components/RegulatorPortal/regulatorPortalRoutes";

/**
 * Get the token expiration time based on login time + ExpiresIn
 * @returns {Date|null} The expiration time or null if token data is missing
 */
export const getTokenExpirationTime = () => {
  const token = JSON.parse(localStorage.getItem("token"));
  const loginTime = localStorage.getItem("lastLogInTime");

  if (!token?.ExpiresIn || !loginTime) return null;

  const loginDate = new Date(loginTime);
  return new Date(loginDate.getTime() + token.ExpiresIn * 1000);
};

/**
 * Check if the token is expiring within the given threshold
 * @param {number} thresholdMs - Threshold in milliseconds (default: 60 seconds)
 * @returns {boolean} True if token is expiring soon
 */
export const isTokenExpiringSoon = (thresholdMs = 60000) => {
  const expiresAt = getTokenExpirationTime();
  if (!expiresAt) return true;
  return (expiresAt - new Date()) < thresholdMs;
};

/**
 * Check if the token has expired
 * @returns {boolean} True if token has expired
 */
export const isTokenExpired = () => {
  const expiresAt = getTokenExpirationTime();
  if (!expiresAt) return true;
  return new Date() > expiresAt;
};

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<Object>} The new token object
 * @throws {Error} If refresh credentials are missing or refresh fails
 */
export const refreshAccessToken = async () => {
  const token = JSON.parse(localStorage.getItem("token"));
  const email = JSON.parse(localStorage.getItem("email"));

  if (!token?.RefreshToken || !email) {
    throw new Error("Missing refresh credentials");
  }

  const response = await PublicAdminPanelInstance.post(
    API_ROUTES.REFRESH_TOKEN,
    { user_name: email, refresh_token: token.RefreshToken }
  );

  const newTokens = {
    ...token,
    AccessToken: response.data.data.AccessToken,
    IdToken: response.data.data.IdToken,
    ExpiresIn: response.data.data.ExpiresIn,
  };

  localStorage.setItem("token", JSON.stringify(newTokens));
  localStorage.setItem("lastLogInTime", new Date().toISOString());

  return newTokens;
};

/**
 * Clear all auth data from localStorage and redirect to login
 */
export const clearAuthAndRedirect = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("email");
  localStorage.removeItem("lastLogInTime");
  window.location.href = "/";
};

// ---------------------------------------------------------------------------
// Affiliate portal (PAMENG-1943)
//
// The portal is a separate sign-in surface (`/affiliate-portal/*`) that targets
// the affiliates Cognito group inside the existing admin pool. Its tokens are
// kept in their own localStorage slot so they don't collide with the staff
// session — an operator can be logged into PAM in one tab and the portal in
// another without stomping on each other.
// ---------------------------------------------------------------------------

export const AFFILIATE_PORTAL_TOKEN_KEY = "affiliate_portal_token";
export const AFFILIATE_PORTAL_LOGIN_TIME_KEY = "affiliate_portal_lastLogInTime";

/** Read the persisted portal token object. */
export const getAffiliatePortalToken = () => {
  const raw = localStorage.getItem(AFFILIATE_PORTAL_TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Persist the portal token bundle returned by `/affiliate-portal/sign-in`. */
export const setAffiliatePortalToken = (token) => {
  localStorage.setItem(AFFILIATE_PORTAL_TOKEN_KEY, JSON.stringify(token));
  localStorage.setItem(AFFILIATE_PORTAL_LOGIN_TIME_KEY, new Date().toISOString());
};

/** Drop the portal session and return to the dedicated Affiliate sign-in. */
export const clearAffiliatePortalAndRedirect = () => {
  localStorage.removeItem(AFFILIATE_PORTAL_TOKEN_KEY);
  localStorage.removeItem(AFFILIATE_PORTAL_LOGIN_TIME_KEY);
  window.location.href = AFFILIATE_PORTAL_SIGN_IN_ROUTE;
};

/** Local-clock expiry of the portal Access/Id tokens (login time + ExpiresIn). */
export const getAffiliatePortalExpirationTime = () => {
  const token = getAffiliatePortalToken();
  const loginTime = localStorage.getItem(AFFILIATE_PORTAL_LOGIN_TIME_KEY);
  if (!token?.ExpiresIn || !loginTime) return null;
  return new Date(new Date(loginTime).getTime() + token.ExpiresIn * 1000);
};

export const isAffiliatePortalTokenExpired = () => {
  const expiresAt = getAffiliatePortalExpirationTime();
  if (!expiresAt) return true;
  return new Date() > expiresAt;
};

/**
 * Decode the payload of a JWT without verifying the signature. We only need
 * the read-only Cognito claims (`cognito:groups`, `custom:affiliate_id`) for
 * routing decisions — verification happens server-side on every request.
 * Returns `null` on any structural failure rather than throwing.
 */
export const decodeIdTokenClaims = (idToken) => {
  if (typeof idToken !== "string") return null;
  const parts = idToken.split(".");
  if (parts.length < 2) return null;
  try {
    // JWT uses base64url; pad to a multiple of 4 and translate the alphabet
    // before handing it to atob.
    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = payload.length % 4;
    if (pad) payload += "=".repeat(4 - pad);
    const decoded = atob(payload);
    // atob returns a binary string; decodeURIComponent + escape handles the
    // UTF-8 round-trip for non-ASCII claim values (emails with accents etc).
    const json = decodeURIComponent(
      decoded
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

/**
 * Cognito groups backing the external (non-staff) portals. The group names are
 * declared here and nowhere else — every predicate below reads from this map.
 */
export const EXTERNAL_PORTAL_GROUPS = Object.freeze({
  AFFILIATES: "affiliates",
  REGULATORS: "regulators",
});

const isCognitoGroupMember = (claims, group) => {
  const groups = claims?.["cognito:groups"];
  return Array.isArray(groups) && groups.includes(group);
};

/** True iff the decoded IdToken belongs to the `affiliates` Cognito group. */
export const isAffiliatesGroupMember = (claims) =>
  isCognitoGroupMember(claims, EXTERNAL_PORTAL_GROUPS.AFFILIATES);

export const hasValidAffiliatePortalSession = () => {
  const token = getAffiliatePortalToken();
  if (!token?.IdToken || isAffiliatePortalTokenExpired()) {
    return false;
  }
  const claims = decodeIdTokenClaims(token.IdToken);
  return !!claims && isAffiliatesGroupMember(claims);
};

/**
 * True iff the decoded token belongs to the `regulators` Cognito group.
 *
 * Useful for post-approval tokens only: a regulator awaiting approval is issued
 * tokens with no group claim at all, so a false result proves nothing about the
 * caller. Regulator routing decisions are made from the authenticated `/me`
 * response (`src/queries/regulator-portal/regulatorMeClient.ts`), never here.
 */
export const isRegulatorsGroupMember = (claims) =>
  isCognitoGroupMember(claims, EXTERNAL_PORTAL_GROUPS.REGULATORS);

// ---------------------------------------------------------------------------
// Regulator portal (TPF-GLI)
//
// Second external sidecar, same storage-isolation rule as the affiliate portal:
// its own localStorage slot so a regulator session and a staff session never
// stomp on each other. Unlike the affiliate portal, nothing here decides
// access — the guard reads `/me`. These helpers only own persistence.
// ---------------------------------------------------------------------------

export const REGULATOR_PORTAL_TOKEN_KEY = "regulator_portal_token";
export const REGULATOR_PORTAL_LOGIN_TIME_KEY =
  "regulator_portal_lastLogInTime";

/** Read the persisted regulator token object. */
export const getRegulatorPortalToken = () => {
  const raw = localStorage.getItem(REGULATOR_PORTAL_TOKEN_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** Persist the regulator token bundle returned by its dedicated sign-in. */
export const setRegulatorPortalToken = (token) => {
  localStorage.setItem(REGULATOR_PORTAL_TOKEN_KEY, JSON.stringify(token));
  localStorage.setItem(
    REGULATOR_PORTAL_LOGIN_TIME_KEY,
    new Date().toISOString(),
  );
};

/** Drop the regulator session without navigating. */
export const clearRegulatorPortalSession = () => {
  localStorage.removeItem(REGULATOR_PORTAL_TOKEN_KEY);
  localStorage.removeItem(REGULATOR_PORTAL_LOGIN_TIME_KEY);
};

/** Drop the regulator session and return to its dedicated sign-in. */
export const clearRegulatorPortalAndRedirect = () => {
  clearRegulatorPortalSession();
  window.location.href = REGULATOR_PORTAL_SIGN_IN_ROUTE;
};

/** Local-clock expiry of the regulator tokens (login time + ExpiresIn). */
export const getRegulatorPortalExpirationTime = () => {
  const token = getRegulatorPortalToken();
  const loginTime = localStorage.getItem(REGULATOR_PORTAL_LOGIN_TIME_KEY);
  if (!token?.ExpiresIn || !loginTime) return null;
  return new Date(new Date(loginTime).getTime() + token.ExpiresIn * 1000);
};

export const isRegulatorPortalTokenExpired = () => {
  const expiresAt = getRegulatorPortalExpirationTime();
  if (!expiresAt) return true;
  return new Date() > expiresAt;
};

/**
 * Structural check only: is there a regulator token we can send to `/me`?
 * Says nothing about whether the caller is actually a regulator.
 */
export const hasRegulatorPortalSession = () => {
  const token = getRegulatorPortalToken();
  return !!token?.AccessToken && !isRegulatorPortalTokenExpired();
};
